import React, { useState, useRef } from 'react';
import { Upload, FileText, Image, Download, Loader2, Trash2, Shield, CheckCircle, AlertCircle, LogOut, Menu, Edit3 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileMenu from '../components/MobileMenu';
import MobileBottomNav from '../components/MobileBottomNav';
import SiteFooter from '../components/SiteFooter';
import PdfTextEditor from '../components/PdfTextEditor';
import '../styles/PdfEditorPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PdfEditorPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [numbersFound, setNumbersFound] = useState(0);
  const [replaceNumber, setReplaceNumber] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState('remover');

  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(selected.type)) {
      setError(t('pdfOnlyFileTypes') || 'Please upload a PDF, JPG, or PNG file only.');
      return;
    }
    if (selected.size > 20 * 1024 * 1024) {
      setError(t('pdfFileTooLarge') || 'File size must be less than 20MB.');
      return;
    }
    setFile(selected);
    setError('');
    setResult(null);
    setNumbersFound(0);
    if (selected.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(selected);
    } else {
      setFilePreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect({ target: { files: [dropped] } });
  };

  const handleProcess = async () => {
    if (!file) return;
    setProcessing(true);
    setError('');
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (replaceNumber.trim()) formData.append('replace_number', replaceNumber.trim());
      const response = await axios.post(`${BACKEND_URL}/api/client/pdfeditor/process`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(response.data);
      setNumbersFound(response.data.numbers_removed || 0);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('pdfProcessingFailed') || 'Failed to process file.';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result?.download_url) return;
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}${result.download_url}`;
    link.download = result.filename || 'cleaned_file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setFile(null);
    setFilePreview(null);
    setResult(null);
    setError('');
    setNumbersFound(0);
    setReplaceNumber('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getFileTypeLabel = () => {
    if (!file) return '';
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type === 'image/png') return 'PNG';
    return 'JPG';
  };

  return (
    <div className="pdfeditor-page" data-testid="pdfeditor-page">
      <Helmet>
        <title>Number Remover Tool - Registan Tourism</title>
        <meta name="description" content="Free online number remover tool for documents. Clean up PDF and image files by removing unwanted numbers instantly. Fast and easy to use." />
        <meta name="keywords" content="number remover, pdf editor, document cleaner, remove numbers from pdf, online document editor" />
        <link rel="canonical" href="https://registantours.com/client/pdfeditor" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/client/pdfeditor" />
        <meta property="og:title" content="Number Remover Tool - Registan Tourism" />
        <meta property="og:description" content="Free online number remover tool for documents. Clean up PDF and image files instantly." />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Number Remover Tool - Registan Tourism" />
        <meta name="twitter:description" content="Free online number remover tool for documents. Remove unwanted numbers from PDF and images instantly." />
      </Helmet>
      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      {/* Main Content */}
      <main className="pdfeditor-main">
        {/* Tab Switcher */}
        <div className="pdfeditor-tabs" data-testid="pdfeditor-tabs">
          <button
            className={`pdfeditor-tab${activeEditorTab === 'remover' ? ' active' : ''}`}
            onClick={() => setActiveEditorTab('remover')}
            data-testid="tab-number-remover"
          >
            <Shield size={18} />
            <span>{t('pdfTitle') || 'Number Remover'}</span>
          </button>
          <button
            className={`pdfeditor-tab${activeEditorTab === 'editor' ? ' active' : ''}`}
            onClick={() => setActiveEditorTab('editor')}
            data-testid="tab-pdf-editor"
          >
            <Edit3 size={18} />
            <span>PDF Text Editor</span>
          </button>
        </div>

        {/* Number Remover Tab */}
        {activeEditorTab === 'remover' && (
        <>
        <div className="pdfeditor-card">
          <div className="pdfeditor-card-header">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <Shield size={36} style={{ color: '#2E7D32' }} />
            </div>
            <h2>{t('pdfTitle') || 'Remove +971 Numbers from Files'}</h2>
            <p>{t('pdfDescription') || 'Upload a PDF, JPG, or PNG file. The system will automatically detect and remove all UAE mobile numbers (+971XXXXXXXXX) and return the cleaned file.'}</p>
          </div>

          {/* Upload Area */}
          {!file && (
            <div
              className="pdfeditor-dropzone"
              data-testid="file-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <Upload size={48} className="dropzone-icon" />
              <p className="dropzone-title">{t('pdfDropzone') || 'Drop your file here or click to browse'}</p>
              <p className="dropzone-hint">{t('pdfDropzoneHint') || 'Supports PDF, JPG, PNG (max 20MB)'}</p>
            </div>
          )}

          {/* File Preview */}
          {file && !result && (
            <div className="pdfeditor-file-preview" data-testid="file-preview">
              <div className="file-info-row">
                <div className="file-icon-box">
                  {file.type === 'application/pdf' ? <FileText size={28} /> : <Image size={28} />}
                </div>
                <div className="file-meta">
                  <span className="file-name">{file.name}</span>
                  <span className="file-details">
                    <span className="file-type-badge">{getFileTypeLabel()}</span>
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <button className="file-remove-btn" onClick={handleReset} data-testid="remove-file-btn">
                  <Trash2 size={18} />
                </button>
              </div>

              {filePreview && (
                <div className="file-image-preview">
                  <img src={filePreview} alt="Preview" />
                </div>
              )}

              {/* Replace Number Input */}
              <div className="replace-number-section" data-testid="replace-number-section">
                <label className="replace-number-label">{t('pdfYourNumber') || 'Your number (optional)'}</label>
                <p className="replace-number-hint">{t('pdfYourNumberHint') || 'Enter your number to replace the removed ones'}</p>
                <input
                  type="tel"
                  className="replace-number-input"
                  placeholder="+971 XX XXX XXXX"
                  value={replaceNumber}
                  onChange={(e) => setReplaceNumber(e.target.value)}
                  data-testid="replace-number-input"
                />
              </div>

              <button className="pdfeditor-process-btn" onClick={handleProcess} disabled={processing} data-testid="process-btn">
                {processing ? (
                  <><Loader2 size={20} className="spin" /> {t('pdfProcessing') || 'Processing...'}</>
                ) : (
                  <><Shield size={20} /> {t('pdfRemoveBtn') || 'Remove Numbers'}</>
                )}
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="pdfeditor-result" data-testid="process-result">
              <div className="result-status">
                <CheckCircle size={48} className="result-icon" />
                <h3>{t('pdfComplete') || 'Processing Complete'}</h3>
                <p>
                  {numbersFound > 0
                    ? result.replaced_with
                      ? `${t('pdfFoundAndReplaced') || 'Found'} ${numbersFound} ${t('pdfNumbers') || 'number(s)'} → ${result.replaced_with}`
                      : `${t('pdfFoundAndRemoved') || 'Found and removed'} ${numbersFound} ${t('pdfNumbers') || 'number(s)'}`
                    : t('pdfNoNumbers') || 'No +971 phone numbers were found in your file.'}
                </p>
              </div>

              {result.numbers_list && result.numbers_list.length > 0 && (
                <div className="numbers-found-list">
                  <h4>{t('pdfNumbersRemoved') || 'Numbers Removed'}:</h4>
                  <div className="number-tags">
                    {result.numbers_list.map((num, i) => (
                      <span key={i} className="number-tag">{num}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="result-actions">
                <button className="pdfeditor-download-btn" onClick={handleDownload} data-testid="download-btn">
                  <Download size={20} /> {t('pdfDownload') || 'Download Cleaned File'}
                </button>
                <button className="pdfeditor-reset-btn" onClick={handleReset} data-testid="new-file-btn">
                  <Upload size={18} /> {t('pdfAnother') || 'Process Another File'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="pdfeditor-error" data-testid="error-message">
              <AlertCircle size={18} /><span>{error}</span>
            </div>
          )}

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} style={{ display: 'none' }} data-testid="file-input" />
        </div>

        <div className="pdfeditor-info">
          <div className="info-item">
            <Shield size={18} />
            <span>{t('pdfSecure') || 'Your files are processed securely and deleted after download'}</span>
          </div>
          <div className="info-item">
            <FileText size={18} />
            <span>{t('pdfSupports') || 'Supports PDF documents, JPG and PNG images'}</span>
          </div>
        </div>
        </>
        )}

        {/* PDF Text Editor Tab */}
        {activeEditorTab === 'editor' && (
          <div className="pdfeditor-card">
            <div className="pdfeditor-card-header">
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <Edit3 size={36} style={{ color: '#2E7D32' }} />
              </div>
              <h2>PDF Text Editor</h2>
              <p>Upload a PDF file, click on any text to edit it, then save and download the modified PDF.</p>
            </div>
            <PdfTextEditor />
          </div>
        )}
      </main>

      <SiteFooter />
      <MobileBottomNav />
    </div>
  );
};

export default PdfEditorPage;
